import React, { useEffect, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelShiftScreen({ onBack }) {
  const [currentShift, setCurrentShift] = useState(null);
  const [openingCash, setOpeningCash] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [note, setNote] = useState("");
  const [report, setReport] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [showEndModal, setShowEndModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const money = (v) => Number(v || 0).toFixed(2);

  const showMsg = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(""), 4000);
  };

  const loadCurrentShift = async () => {
    try {
      setLoading(true);

      const res = await hotelApi.get("/shifts/current");
      const shift = res.data?.shift || null;

      setCurrentShift(shift);

      if (shift?.id) {
        const rep = await hotelApi.get(`/shifts/${shift.id}/report`);
        setReport(rep.data || null);
      } else {
        setReport(null);
      }
    } catch (e) {
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to load shift.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentShift();
  }, []);

  const startShift = async () => {
    try {
      setLoading(true);

      await hotelApi.post("/shifts/start", {
        opening_cash: Number(openingCash || 0),
        note,
      });

      setOpeningCash("");
      setNote("");
      showMsg("✅ Shift started successfully.");
      await loadCurrentShift();
    } catch (e) {
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to start shift.")
      );
    } finally {
      setLoading(false);
    }
  };

  const endShift = async () => {
    if (!currentShift?.id) {
      showMsg("❌ No active shift found.");
      return;
    }

    if (confirmText !== "END") {
      showMsg("❌ Type END to confirm shift closure.");
      return;
    }

    try {
      setLoading(true);

      await hotelApi.post("/shifts/end", {
        shift_id: currentShift.id,
        closing_cash: Number(closingCash || 0),
        note,
        mark_all_seen: true,
        confirm_text: confirmText,
      });

      setClosingCash("");
      setNote("");
      setConfirmText("");
      setShowEndModal(false);

      showMsg("✅ Shift ended. All current standing records marked as seen.");
      await loadCurrentShift();
    } catch (e) {
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to end shift.")
      );
    } finally {
      setLoading(false);
    }
  };

  const markShiftSeen = async () => {
    if (!currentShift?.id) return;

    try {
      setLoading(true);

      await hotelApi.post(`/shifts/${currentShift.id}/mark-seen`);

      showMsg("✅ Shift marked as seen.");
      await loadCurrentShift();
    } catch (e) {
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to mark shift as seen.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={{ margin: 0 }}>Shift Management</h2>
          <div style={styles.subText}>Start, monitor, and end hotel shifts.</div>
        </div>

        <div style={styles.actions}>
          <button style={styles.btn} onClick={loadCurrentShift} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button style={styles.btn2} onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      {!currentShift ? (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Start New Shift</div>

          <label style={styles.label}>Opening Cash</label>
          <input
            type="number"
            style={styles.input}
            value={openingCash}
            onChange={(e) => setOpeningCash(e.target.value)}
            placeholder="0.00"
          />

          <label style={styles.label}>Opening Note</label>
          <textarea
            style={styles.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
          />

          <button style={styles.primary} onClick={startShift} disabled={loading}>
            Start Shift
          </button>
        </div>
      ) : (
        <>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Current Open Shift</div>

            <div style={styles.summaryGrid}>
              <SummaryCard title="Shift Code" value={currentShift.shift_code || "—"} />
              <SummaryCard title="Opened By" value={currentShift.opened_by || "—"} />
              <SummaryCard title="Opened At" value={currentShift.opened_at || "—"} />
              <SummaryCard title="Opening Cash" value={money(currentShift.opening_cash)} />
              <SummaryCard title="Status" value={currentShift.status || "—"} />
              <SummaryCard
                title="Admin Seen"
                value={Number(currentShift.admin_seen) === 1 ? "Seen" : "Not Seen"}
              />
            </div>

            <div style={styles.actions}>
              <button style={styles.btn} onClick={markShiftSeen} disabled={loading}>
                Mark Shift Seen
              </button>
            </div>
          </div>

          {report && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>Live Shift Report</div>

              <div style={styles.summaryGrid}>
                <SummaryCard title="Room Revenue" value={money(report.summary?.room_revenue)} />
                <SummaryCard title="Supplies Revenue" value={money(report.summary?.supplies_revenue)} />
                <SummaryCard title="Gross Revenue" value={money(report.summary?.gross_revenue)} />
                <SummaryCard title="Cash" value={money(report.summary?.cash_total)} />
                <SummaryCard title="Mpesa" value={money(report.summary?.mpesa_total)} />
                <SummaryCard title="Bank" value={money(report.summary?.bank_total)} />
                <SummaryCard title="Total Paid" value={money(report.summary?.total_paid)} />
                <SummaryCard title="Outstanding" value={money(report.summary?.total_balance)} />
              </div>

              <ReportTable
                title="Payments"
                rows={report.payments || []}
                columns={[
                  ["id", "ID"],
                  ["created_at", "Date"],
                  ["payment_method", "Method"],
                  ["amount", "Amount"],
                  ["received_by", "Received By"],
                ]}
              />

              <ReportTable
                title="Stays"
                rows={report.stays || []}
                columns={[
                  ["id", "Stay ID"],
                  ["guest_name", "Guest"],
                  ["room_no", "Room"],
                  ["checkin_at", "Check-In"],
                  ["expected_checkout", "Expected Checkout"],
                  ["grand_total", "Total"],
                  ["amount_paid", "Paid"],
                  ["balance", "Balance"],
                  ["status", "Status"],
                ]}
              />

              <ReportTable
                title="Supplies Sold"
                rows={report.supplies || []}
                columns={[
                  ["sale_id", "Sale ID"],
                  ["created_at", "Date"],
                  ["guest_name", "Guest"],
                  ["room_no", "Room"],
                  ["item_name", "Item"],
                  ["qty", "Qty"],
                  ["line_total", "Total"],
                  ["sold_by", "Sold By"],
                ]}
              />
            </div>
          )}

          <div style={styles.card}>
            <div style={styles.cardTitle}>End / Change Shift</div>

            <label style={styles.label}>Closing Cash</label>
            <input
              type="number"
              style={styles.input}
              value={closingCash}
              onChange={(e) => setClosingCash(e.target.value)}
              placeholder="0.00"
            />

            <label style={styles.label}>Closing Note</label>
            <textarea
              style={styles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note"
            />

            <button
              style={styles.dangerBtn}
              onClick={() => setShowEndModal(true)}
              disabled={loading}
            >
              End Shift / Change Shift
            </button>

            <div style={styles.warningText}>
              This will close the current shift and mark all current standing records as seen.
            </div>
          </div>
        </>
      )}

      {showEndModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalBox}>
            <div style={styles.modalTitle}>Confirm Shift Closure</div>

            <p style={styles.modalText}>
              Type <strong>END</strong> to close or change this shift.
            </p>

            <input
              type="text"
              style={styles.input}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Type END"
              autoFocus
            />

            <div style={styles.modalActions}>
              <button
                style={styles.btn}
                onClick={() => {
                  setShowEndModal(false);
                  setConfirmText("");
                }}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                style={{
                  ...styles.dangerBtn,
                  opacity: confirmText === "END" ? 1 : 0.5,
                  cursor: confirmText === "END" ? "pointer" : "not-allowed",
                }}
                disabled={loading || confirmText !== "END"}
                onClick={endShift}
              >
                {loading ? "Closing..." : "Confirm End Shift"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div style={styles.summaryCard}>
      <div style={styles.summaryTitle}>{title}</div>
      <div style={styles.summaryValue}>{value}</div>
    </div>
  );
}

function ReportTable({ title, rows, columns }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.sectionTitle}>{title}</div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map(([key, label]) => (
                <th key={key} style={styles.th}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td style={styles.td} colSpan={columns.length}>
                  No records found.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.id || row.sale_id || index}>
                  {columns.map(([key]) => (
                    <td key={key} style={styles.td}>
                      {row[key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    padding: 12,
    background: "#f8fafc",
    minHeight: "100vh",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  subText: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 12,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)",
    marginBottom: 12,
  },
  cardTitle: {
    fontWeight: 900,
    marginBottom: 12,
    color: "#0f172a",
    fontSize: 18,
  },
  sectionTitle: {
    fontWeight: 900,
    marginBottom: 8,
    color: "#0f172a",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  summaryCard: {
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,.10)",
    borderRadius: 14,
    padding: 12,
  },
  summaryTitle: {
    fontWeight: 800,
    marginBottom: 6,
    color: "#475569",
    fontSize: 12,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
    wordBreak: "break-word",
  },
  label: {
    display: "block",
    marginBottom: 6,
    marginTop: 10,
    fontWeight: 800,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
  },
  textarea: {
    width: "100%",
    minHeight: 80,
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
    resize: "vertical",
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn2: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#f1f5f9",
    fontWeight: 800,
    cursor: "pointer",
  },
  primary: {
    marginTop: 12,
    padding: "11px 14px",
    borderRadius: 12,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  dangerBtn: {
    marginTop: 12,
    padding: "11px 14px",
    borderRadius: 12,
    border: "none",
    background: "#dc2626",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  warningText: {
    marginTop: 8,
    fontSize: 13,
    color: "#991b1b",
    fontWeight: 700,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#475569",
    borderBottom: "1px solid rgba(15,23,42,.12)",
    padding: "9px 8px",
    whiteSpace: "nowrap",
    background: "#f8fafc",
  },
  td: {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(15,23,42,.08)",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
  },
  modalBox: {
    background: "#fff",
    padding: 24,
    borderRadius: 16,
    width: 430,
    maxWidth: "95%",
    boxShadow: "0 20px 50px rgba(0,0,0,.3)",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: "#0f172a",
    marginBottom: 10,
  },
  modalText: {
    color: "#334155",
    fontWeight: 700,
    marginBottom: 12,
  },
  modalActions: {
    marginTop: 16,
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
};